/**
 * DocumentComposerPanel.jsx
 * ==========================
 * AI Document Composer for RFPs, Contracts, SOPs, etc.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  FileEdit, Sparkles, Loader2, Copy, Download, 
  FileText, Languages, Building 
} from 'lucide-react';

import { copyToClipboard as clipboardCopy } from '../../utils/clipboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentComposerPanel = () => {
  const [docType, setDocType] = useState('rfp');
  const [language, setLanguage] = useState('en');
  const [context, setContext] = useState('');
  const [templates, setTemplates] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/composer/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTemplates(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const generateDocument = async () => {
    setGenerating(true);
    setResult(null);

    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/composer/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          doc_type: docType,
          language,
          context: context ? { user_context: context } : {}
        })
      });

      if (!response.ok) throw new Error('Generation failed');
      
      const data = await response.json();
      setResult(data);
      toast.success('Document generated successfully!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (result?.content_preview) {
      clipboardCopy(result.content_preview).then(ok => {
        if (ok) toast.success('Copied to clipboard!');
        else toast.error('Copy failed');
      });
    }
  };

  return (
    <Card data-testid="document-composer-panel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileEdit className="h-5 w-5 text-primary" />
          <CardTitle>AI Document Composer</CardTitle>
        </div>
        <CardDescription>
          Generate professional documents with AI assistance
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Document Type Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Document Type</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rfp">Request for Proposal</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="sop">Standard Operating Procedure</SelectItem>
                <SelectItem value="letter">Business Letter</SelectItem>
                <SelectItem value="policy">Policy Document</SelectItem>
                <SelectItem value="memo">Memorandum</SelectItem>
                <SelectItem value="report">Business Report</SelectItem>
                <SelectItem value="proposal">Business Proposal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Language</label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger>
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="hi">Hindi</SelectItem>
                <SelectItem value="bn">Bengali</SelectItem>
                <SelectItem value="ta">Tamil</SelectItem>
                <SelectItem value="te">Telugu</SelectItem>
                <SelectItem value="mr">Marathi</SelectItem>
                <SelectItem value="es">Spanish</SelectItem>
                <SelectItem value="fr">French</SelectItem>
                <SelectItem value="de">German</SelectItem>
                <SelectItem value="ar">Arabic</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Context Input */}
        <div>
          <label className="text-sm font-medium mb-2 block">Context & Requirements</label>
          <Textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Describe what you need... e.g., 'RFP for cloud migration services, budget $500k, timeline 6 months'"
            rows={4}
          />
        </div>

        {/* Generate Button */}
        <Button 
          onClick={generateDocument} 
          disabled={generating}
          className="w-full"
          data-testid="generate-document-btn"
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Document
            </>
          )}
        </Button>

        {/* Result */}
        {result && (
          <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="font-medium">Generated Document</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copyToClipboard}>
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </Button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Badge variant="outline">{result.doc_type}</Badge>
              <Badge variant="secondary">{result.language}</Badge>
            </div>

            <div className="bg-background rounded p-3 text-sm whitespace-pre-wrap max-h-96 overflow-y-auto">
              {result.content_preview}
            </div>

            <p className="text-xs text-muted-foreground">
              Workspace ID: {result.workspace_id}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentComposerPanel;
